import { Box, Tooltip, Typography } from "@mui/material";
import { Flex, UserAvatar, colors } from "@zfp-akademie-a-s/zfp-design";
import type { AnalysisOverviewItemSharing, DetailWithCurrentUser } from "Infrastructure/ApiZfp/Schema";
import { useUserContext } from "Infrastructure/Context/UserContext";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type AnalysisAccessAdvisor, getAnalysisAccessAdvisors } from "./AnalysesAccessCell.helpers";

const MAX_VISIBLE_ADVISORS = 3;
const MORE_SIZE = 24;

type Props = {
  readonly sharing: AnalysisOverviewItemSharing;
  readonly agentsByGuid: ReadonlyMap<string, DetailWithCurrentUser>;
};

// the tooltip is portaled, but React still bubbles its clicks up to the row, which would open the detail
const stopRowClick = (event: React.MouseEvent) => event.stopPropagation();

const tooltipSlotProps = { tooltip: { sx: { whiteSpace: "pre-line" } } };

type AvatarProps = {
  readonly advisor: AnalysisAccessAdvisor;
  readonly label: string;
};

const AccessAdvisorAvatar: React.FC<AvatarProps> = ({ advisor, label }) => (
  <Tooltip title={label} placement="top" arrow slotProps={tooltipSlotProps}>
    <Flex
      role="img"
      tabIndex={0}
      aria-label={label}
      borderRadius="50%"
      sx={(theme) => ({
        // the current advisor's avatar border turns orange and 1px wider: a harder edge, not a ring around it
        ...(advisor.isCurrent && {
          boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
          "& .MuiAvatar-root": { borderColor: "primary.main" },
        }),
        ...(!advisor.isCurrent && {
          filter: "grayscale(1) opacity(.5)",
          transition: "filter .15s",
          "&:hover, &:focus-visible": { filter: "none" },
        }),
      })}
    >
      <UserAvatar
        firstName={advisor.firstName}
        lastName={advisor.lastName}
        fullName={advisor.fullName}
        photoUrl={advisor.imageUrl}
        color={advisor.isCurrent ? "primary" : "secondary"}
        size="xs"
      />
    </Flex>
  </Tooltip>
);

export const AnalysesAccessCell: React.FC<Props> = ({ sharing, agentsByGuid }) => {
  const { t } = useResource();
  const { user } = useUserContext();

  const advisors = getAnalysisAccessAdvisors({ sharing, agentsByGuid, currentUser: user });

  if (advisors.length === 0) {
    return null;
  }

  const visibleAdvisors = advisors.slice(0, MAX_VISIBLE_ADVISORS);
  const hiddenAdvisors = advisors.slice(MAX_VISIBLE_ADVISORS);
  const hiddenAdvisorsLabel = hiddenAdvisors.map((advisor) => advisor.fullName).join("\n");

  const getAdvisorLabel = (advisor: AnalysisAccessAdvisor) => {
    const name = t(Resources.AnalysesListing.Table.Access.Advisor, { advisorName: advisor.fullName, advisorId: advisor.ssoId });
    const owner = advisor.isOwner ? [t(Resources.AnalysesListing.Table.Access.Owner)] : [];
    const role = t(advisor.isCurrent ? Resources.AnalysesListing.Table.Access.Current : Resources.AnalysesListing.Table.Access.Past);
    return [name, ...owner, role].join("\n");
  };

  return (
    <Flex
      component="ul"
      aria-label={t(Resources.AnalysesListing.Table.Access.ListLabel)}
      alignItems="center"
      gap={Spacing.S}
      m={0}
      p={0}
      sx={{ listStyle: "none" }}
    >
      {visibleAdvisors.map((advisor) => (
        <Box component="li" key={advisor.guid} display="flex" onClick={stopRowClick}>
          <AccessAdvisorAvatar advisor={advisor} label={getAdvisorLabel(advisor)} />
        </Box>
      ))}
      {hiddenAdvisors.length > 0 && (
        <Box component="li" display="flex" onClick={stopRowClick}>
          <Tooltip title={hiddenAdvisorsLabel} placement="top" arrow slotProps={tooltipSlotProps}>
            <Flex
              role="img"
              tabIndex={0}
              aria-label={hiddenAdvisorsLabel}
              alignItems="center"
              justifyContent="center"
              height={MORE_SIZE}
              minWidth={MORE_SIZE}
              px={Spacing.XS}
              borderRadius="999px"
              sx={{ backgroundColor: colors.textTertiary }}
            >
              <Typography variant="caption" fontWeight={700} sx={{ color: colors.darkGrey }}>
                +{hiddenAdvisors.length}
              </Typography>
            </Flex>
          </Tooltip>
        </Box>
      )}
    </Flex>
  );
};
